import {
  Button,
  FeedCardGrid,
  FeedCardGridItem,
  Icon,
  TextInput,
  FilterBar,
} from '@socialchorus/shared-ui-components';
import { FC, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDebounce } from 'usehooks-ts';
import { DEFAULT_SKIP_TO_CONTENT_ID } from '../../../../../common/use-skip-to-content';
import EmptyFeed from '../../../../feed/empty-feed';
import EndOfFeed from '../../../../feed/end-of-feed';
import { Spinner } from '../../../../ui';
import ViewTrigger from '../../../../view-trigger';
import { BaseTab } from '../BaseTab';
import { FeedCardWithData } from '../../../../content/solar-feed-card/solar-feed-card';
import styles from './styles.module.scss';
import EmptyPlaceholder from '../../common/EmptyPlaceholder';
import useFeatureFlag from '../../../../../common/use-feature-flag';
import { Feature } from '../../../../../models/features/features';
import { Filter as FilterBarFilter } from '@socialchorus/shared-ui-components/dist/components/FilterBar/filters';
import { FilterBarValue } from '@socialchorus/shared-ui-components/dist/components/FilterBar';
import { useChannelSearchMeta } from '../../../../../common/queries/use-channel-search-meta';
import { useContentFeedQuery } from '../../../../../common/queries/use-content-feed-query';
import { convertToFilterBarFilters } from './util';

interface Props {
  topicId: string;
  analyticsData: IAnalyticsData;
  activeTabId: number;
}

const PostsTab: FC<Props> = ({ topicId, analyticsData, activeTabId }) => {
  const { t } = useTranslation();

  const [hasLoaded, setHasLoaded] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const debouncedSearchQuery = useDebounce(searchQuery, 500);

  const searchInputRef = useRef<HTMLInputElement>(null);
  const resetSearch = () => {
    setSearchQuery('');
    searchInputRef.current?.focus();
  };

  const isFiltersEnabled = useFeatureFlag(
    Feature.KNOWLEDGE_HUB_FILTERS_ENABLED
  );
  const [filters, setFilters] = useState<FilterBarFilter[]>([]);
  const [defaultFilters, setDefaultFilters] = useState<string[]>([]);
  const [searchFilters, setSearchFilters] = useState<
    Record<string, string | string[]>
  >({});
  const { data: metadata } = useChannelSearchMeta(topicId); // useCategoryMetadata();

  useEffect(() => {
    if (metadata) {
      const topicMetadata = metadata.categories[0];
      if (topicMetadata?.filters) {
        setFilters(convertToFilterBarFilters(topicMetadata.filters));
        setDefaultFilters(topicMetadata.defaultFilters || []);
      }
    }
  }, [metadata]);

  const handleFilterUpdate = (selectedFilters: FilterBarValue[]) => {
    if (selectedFilters.length === 0) {
      resetSearch();
      return;
    }
    const newSearchFilters: Record<string, string | string[]> = {};
    selectedFilters.forEach((filterValue) => {
      if (filterValue.parameter === 'search') {
        setSearchQuery(filterValue.value as string);
      } else {
        if (
          (filterValue.value instanceof Array &&
            filterValue.value.length > 0) ||
          (typeof filterValue.value === 'string' && filterValue.value !== '')
        ) {
          newSearchFilters[filterValue.parameter] = filterValue.value;
        }
      }
    });
    setSearchFilters(newSearchFilters);
  };

  const {
    isLoading,
    isFetchingNextPage,
    data: feedItems,
    rawQueryData,
    hasNextPage: canLoadMore,
    fetchNextPage: fetchFeed,
  } = useContentFeedQuery({
    channelId: topicId,
    // If the search query has been cleared, use the empty value to prevent delay
    query: searchQuery.length === 0 ? searchQuery : debouncedSearchQuery,
    pinned: true,
    homepageModuleParams: searchFilters,
  });

  const fetching = isLoading || isFetchingNextPage;

  const total = rawQueryData?.pages?.[0]?.meta.total;

  useEffect(() => {
    if (!hasLoaded && !fetching) {
      setHasLoaded(true);
    }
  }, [fetching, hasLoaded]);

  // If the search query has been cleared, set this variable to `false` instead of waiting for the debounced value
  const hasQuery =
    (searchQuery.length > 0 && debouncedSearchQuery.length > 0) ||
    Object.keys(searchFilters).length > 0;
  const hasNoPostsInTopic =
    !hasQuery && !fetching && feedItems && feedItems.length === 0;

  if (hasNoPostsInTopic) {
    return (
      <BaseTab activeTabId={activeTabId} topicId={topicId}>
        <EmptyPlaceholder
          title={t('screens.topic_page.no_posts.title')}
          description={t('screens.topic_page.no_posts.description')}
        />
      </BaseTab>
    );
  }

  return (
    <BaseTab activeTabId={activeTabId} topicId={topicId}>
      {/* Hide search box before first load and only display when there's content in the topic */}
      {hasLoaded && !isFiltersEnabled && (
        <div className={styles.SearchBox__Wrapper}>
          <TextInput
            type="search"
            ref={searchInputRef}
            leftWidget={<Icon size={14}>search</Icon>}
            placeholder={t('screens.topic_page.searchbox_placeholder')}
            size="compact"
            value={searchQuery}
            onChange={(val) => setSearchQuery(val || '')}
          />
          {hasQuery && !fetching && (
            <span
              role="status"
              aria-live="polite"
              className="search-box--result-status"
            >
              {t('screens.topic_page.search_results.count', { count: total })}
            </span>
          )}
        </div>
      )}
      {hasLoaded && isFiltersEnabled && (
        <FilterBar
          searchEnabled
          dropdownArrowPosition="right"
          variant="compact"
          filterSearchEnabled
          className={styles.FilterBar}
          filters={filters}
          selected={defaultFilters}
          onUpdate={handleFilterUpdate}
          dropdownClassName={styles.FilterBar__Dropdown}
        />
      )}

      {!fetching && hasQuery && total === 0 ? (
        <EmptyPlaceholder
          title={t('screens.topic_page.search_results.no_results.title')}
          description={t(
            'screens.topic_page.search_results.no_results.description',
            {
              query: debouncedSearchQuery,
            }
          )}
          cta={
            <Button
              variant="primary"
              label={t('content.clear_search')}
              onClick={resetSearch}
            />
          }
        />
      ) : (
        <section
          className={styles.FeedCardList}
          id={DEFAULT_SKIP_TO_CONTENT_ID}
        >
          <FeedCardGrid columns={1}>
            {feedItems?.map((content) => (
              <FeedCardGridItem key={content.id}>
                <FeedCardWithData
                  content={content}
                  analyticsData={analyticsData}
                  layout="horizontal"
                />
              </FeedCardGridItem>
            ))}
          </FeedCardGrid>
          {fetching ? (
            <Spinner />
          ) : canLoadMore ? (
            <ViewTrigger offset="600px" onInview={() => fetchFeed()} />
          ) : feedItems?.length ? (
            <EndOfFeed />
          ) : (
            <EmptyFeed />
          )}
        </section>
      )}
    </BaseTab>
  );
};

export default PostsTab;
